/**
 * What the claim knows, answer by answer, and where each answer came from.
 *
 * A fact is keyed by its registry id. Nothing is ever a fact by default: an
 * entry with no fact is open, and only the family (or the batch check, which
 * the family confirms) turns an open entry into an answer.
 */

/** A table's rows, keyed by column id. */
export type Row = Record<string, string>;
export type Value = string | string[] | Row[];

export type FactState = 'confirmed' | 'proposed' | 'conflict' | 'skipped';

export type SourceId =
  | 'profile'
  | 'account'
  | 'voice'
  | 'typed'
  | 'prescription'
  | 'hospital_letter'
  | 'care_plan'
  | 'discharge_summary'
  | 'authority_document'
  | 'call_notes'
  | 'nhs'
  | 'batch_check'
  | 'family'
  | 'adviser'
  | 'derived';

export type Fact = {
  value: Value;
  state: FactState;
  source: SourceId;
  /** The words or document line this came from. */
  quote?: string;
  /** The other reading, while two sources disagree. */
  rival?: { value: Value; source: SourceId; quote?: string };
};

export type Facts = Record<string, Fact>;

export const SOURCE_LABEL: Record<SourceId, string> = {
  profile: 'Her care profile',
  account: 'Your KareHero account',
  voice: 'Your voice note',
  typed: 'What you typed',
  prescription: 'Her prescription',
  hospital_letter: 'Hospital or clinic letter',
  care_plan: 'Care plan',
  discharge_summary: 'Discharge summary',
  authority_document: 'Authority document',
  call_notes: 'Your Care Adviser call',
  nhs: 'NHS App',
  batch_check: 'Batch check',
  family: 'Your answer',
  adviser: 'Your Care Adviser',
  derived: 'Worked out from your answers',
};

export const isEmpty = (value: Value | undefined): boolean =>
  value === undefined ||
  (typeof value === 'string' ? value.trim() === '' : value.length === 0);

/** The value used to decide what the form asks next. A skip decides nothing. */
export const valueOf = (facts: Facts, id: string): Value | undefined => {
  const fact = facts[id];
  if (!fact || fact.state === 'skipped' || fact.state === 'conflict')
    return undefined;
  return isEmpty(fact.value) ? undefined : fact.value;
};

export const text = (facts: Facts, id: string): string => {
  const value = valueOf(facts, id);
  return typeof value === 'string' ? value : '';
};

/** The family's own answer: confirmed, and it settles any disagreement. */
export const answer = (
  facts: Facts,
  id: string,
  value: Value,
  source: SourceId = 'family',
): Facts => ({ ...facts, [id]: { value, state: 'confirmed', source } });

export const answerAll = (
  facts: Facts,
  values: Record<string, Value>,
  source: SourceId = 'family',
): Facts =>
  Object.entries(values).reduce(
    (next, [id, value]) => answer(next, id, value, source),
    facts,
  );

/**
 * A suggestion from a recording, document or earlier call. A family answer is
 * never overwritten. When a second source disagrees with a proposal or a
 * profile fact, the fact becomes a conflict carrying both readings.
 */
export const propose = (
  facts: Facts,
  id: string,
  value: Value,
  source: SourceId,
  quote?: string,
): Facts => {
  const current = facts[id];
  if (current?.source === 'family' && current.state === 'confirmed')
    return facts;
  if (
    current &&
    current.state !== 'skipped' &&
    !isEmpty(current.value) &&
    JSON.stringify(current.value) !== JSON.stringify(value)
  ) {
    return {
      ...facts,
      [id]: { ...current, state: 'conflict', rival: { value, source, quote } },
    };
  }
  return { ...facts, [id]: { value, state: 'proposed', source, quote } };
};

export const skip = (facts: Facts, id: string): Facts => ({
  ...facts,
  [id]: { value: '', state: 'skipped', source: 'family' },
});

export const confirm = (facts: Facts, id: string): Facts => {
  const fact = facts[id];
  if (!fact || fact.state === 'conflict') return facts;
  return { ...facts, [id]: { ...fact, state: 'confirmed' } };
};

/** Settle a conflict by choosing one reading; the choice is the family's. */
export const settle = (facts: Facts, id: string, keep: 'current' | 'rival') => {
  const fact = facts[id];
  if (!fact?.rival) return facts;
  const chosen = keep === 'current' ? fact : { ...fact.rival };
  return {
    ...facts,
    [id]: {
      value: chosen.value,
      source: chosen.source,
      quote: chosen.quote,
      state: 'confirmed' as const,
    },
  };
};
